import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/es';
import { db } from '../firebaseConfig';
import { collection, addDoc, Timestamp, onSnapshot, query, where, deleteDoc, doc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { IoAddCircle, IoTrash, IoCalendarOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { getProviderSchedule, isDayWorking } from '../utils/availabilityService';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CalendarioProveedorScreen.css';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';

// Setup Localizer
moment.locale('es');
const localizer = momentLocalizer(moment);

const messages = {
    allDay: 'Todo el día',
    previous: 'Anterior',
    next: 'Siguiente',
    today: 'Hoy',
    month: 'Mes',
    week: 'Semana',
    day: 'Día',
    agenda: 'Agenda',
    date: 'Fecha',
    time: 'Hora',
    event: 'Evento',
    noEventsInRange: 'Sin eventos este periodo.',
};

function CalendarioProveedorScreen() {
    const { userData } = useAuth();
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [providerSchedule, setProviderSchedule] = useState(null);

    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [newEventTitle, setNewEventTitle] = useState('');
    const [newEventDate, setNewEventDate] = useState(moment().format('YYYY-MM-DD'));
    const [newEventStart, setNewEventStart] = useState('09:00');
    const [newEventEnd, setNewEventEnd] = useState('11:00');
    const [eventType] = useState('personal');
    const [selectedEvent, setSelectedEvent] = useState(null);

    // Controlled Calendar State
    const [currentDate, setCurrentDate] = useState(new Date());
    const [currentView, setCurrentView] = useState('month');

    // 0. Fetch Schedule separately
    useEffect(() => {
        if (userData?.proveedorId) {
            getProviderSchedule(userData.proveedorId).then(sched => {
                setProviderSchedule(sched);
            });
        }
    }, [userData]);

    // 1. Listen to Quotes & Events
    useEffect(() => {
        if (!userData?.proveedorId) {
            setLoading(false);
            return;
        }

        const proveedorId = userData.proveedorId;

        // Quotes Query
        const qQuotes = query(
            collection(db, "cotizaciones"),
            where("proveedorId", "==", proveedorId),
            where("estatus", "in", ["servicio_programado", "proveedor_en_camino", "proveedor_en_sitio", "servicio_en_curso", "esperando_aprobacion_final", "pendiente_residente"])
        );

        const unsubQuotes = onSnapshot(qQuotes, (snapshot) => {
            let quoteEvents = [];

            snapshot.docs.forEach(doc => {
                const data = doc.data();
                if (!data.fechaHoraServicioSugerido) return;

                const start = data.fechaHoraServicioSugerido.toDate();
                let duration = 2; // hours default

                if (data.tiempoServicio) {
                    const str = data.tiempoServicio.toLowerCase();
                    if (str.includes('día') || str.includes('dia')) {
                        const match = str.match(/(\d+)\s*d/);
                        if (match) duration = parseInt(match[1]) * 24;
                    } else if (str.includes('minuto')) {
                        const match = str.match(/(\d+)\s*m/);
                        if (match) duration = parseInt(match[1]) / 60;
                    } else {
                        const match = str.match(/(\d+)\s*hora/);
                        if (match) duration = parseInt(match[1]);
                    }
                }

                const totalEnd = moment(start).add(duration, 'hours');

                if (providerSchedule) {
                    let currentDay = moment(start).startOf('day');
                    const endDay = moment(totalEnd).endOf('day');

                    while (currentDay.isBefore(endDay)) {
                        const dayStr = currentDay.format('YYYY-MM-DD');

                        if (isDayWorking(dayStr, providerSchedule)) {
                            const addSegment = (sTime, eTime) => {
                                const workStart = moment(`${currentDay.format('YYYY-MM-DD')}T${sTime}`);
                                const workEnd = moment(`${currentDay.format('YYYY-MM-DD')}T${eTime}`);
                                const segStart = moment.max(moment(start), workStart);
                                const segEnd = moment.min(totalEnd, workEnd);

                                if (segStart.isBefore(segEnd)) {
                                    quoteEvents.push({
                                        id: doc.id,
                                        title: `🛠 ${data.tipoServicio} (${data.nombreResidente})`,
                                        start: segStart.toDate(),
                                        end: segEnd.toDate(),
                                        resource: data,
                                        type: 'service',
                                        color: data.estatus === 'pendiente_residente' ? '#eab308' : '#7B61FF'
                                    });
                                }
                            };

                            if (providerSchedule.hasOwnProperty('habilitado') || providerSchedule.hasOwnProperty('horaInicio')) {
                                addSegment(providerSchedule.horaInicio || '09:00', providerSchedule.horaFin || '18:00');
                            } else {
                                const dayIndex = currentDay.day();
                                const daysMap = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
                                const dayNameNorm = daysMap[dayIndex].normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
                                const scheduleKey = Object.keys(providerSchedule).find(k => k.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() === dayNameNorm);
                                if (scheduleKey) {
                                    const config = providerSchedule[scheduleKey];
                                    if (config.tipo === 'corrido') {
                                        addSegment(config.inicio1 || '09:00', config.fin1 || '18:00');
                                    } else {
                                        addSegment(config.inicio1 || '09:00', config.fin1 || '14:00');
                                        addSegment(config.inicio2 || '16:00', config.fin2 || '19:00');
                                    }
                                }
                            }
                        }
                        currentDay.add(1, 'day');
                    }
                } else {
                    quoteEvents.push({
                        id: doc.id,
                        title: `🛠 ${data.tipoServicio} (${data.nombreResidente})`,
                        start: start,
                        end: totalEnd.toDate(),
                        resource: data,
                        type: 'service',
                        color: data.estatus === 'pendiente_residente' ? '#eab308' : '#7B61FF'
                    });
                }
            });

            const qPersonal = query(collection(db, "eventos_proveedor"), where("proveedorId", "==", proveedorId));
            const unsubPersonal = onSnapshot(qPersonal, (snapPersonal) => {
                const personalEvents = snapPersonal.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        title: `🔒 ${data.titulo}`,
                        start: data.fechaInicio.toDate(),
                        end: data.fechaFin.toDate(),
                        type: 'personal',
                        color: '#64748b'
                    };
                });
                setEvents([...quoteEvents, ...personalEvents]);
                setLoading(false);
            });
            return () => unsubPersonal();
        });

        return () => unsubQuotes();
    }, [userData, providerSchedule]);

    const dayPropGetter = (date) => {
        if (!providerSchedule) return {};
        const dayStr = moment(date).format('YYYY-MM-DD');
        if (!isDayWorking(dayStr, providerSchedule)) {
            return { style: { backgroundColor: '#f1f5f9', opacity: 0.5 } };
        }
        return {};
    };

    const slotPropGetter = (date) => {
        if (!providerSchedule) return {};
        const dayStr = moment(date).format('YYYY-MM-DD');
        if (!isDayWorking(dayStr, providerSchedule)) return { style: { backgroundColor: '#f1f5f9' } };
        
        const timeVal = moment(date).format('HH:mm');
        const checkRange = (s, e) => (timeVal >= s && timeVal < e);
        let isWorkingHour = false;
        
        if (providerSchedule.hasOwnProperty('habilitado') || providerSchedule.hasOwnProperty('horaInicio')) {
            isWorkingHour = checkRange(providerSchedule.horaInicio || '09:00', providerSchedule.horaFin || '18:00');
        } else {
            const dayIndex = moment(date).day();
            const daysMap = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
            const dayNameNorm = daysMap[dayIndex].normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
            const scheduleKey = Object.keys(providerSchedule).find(k => k.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() === dayNameNorm);
            if (scheduleKey) {
                const config = providerSchedule[scheduleKey];
                if (config.tipo === 'corrido') {
                    isWorkingHour = checkRange(config.inicio1 || '09:00', config.fin1 || '18:00');
                } else {
                    isWorkingHour = checkRange(config.inicio1 || '09:00', config.fin1 || '14:00') ||
                                    checkRange(config.inicio2 || '16:00', config.fin2 || '19:00');
                }
            }
        }
        if (!isWorkingHour) return { style: { backgroundColor: '#f8fafc' } };
        return {};
    };

    const handleAddEvent = async () => {
        if (!newEventTitle || !newEventDate || !newEventStart || !newEventEnd) return alert("Completa los campos.");
        const start = moment(`${newEventDate}T${newEventStart}`).toDate();
        const end = moment(`${newEventDate}T${newEventEnd}`).toDate();
        if (end <= start) return alert("La hora fin debe ser mayor a la inicio.");
        try {
            await addDoc(collection(db, "eventos_proveedor"), {
                proveedorId: userData.proveedorId,
                titulo: newEventTitle,
                fechaInicio: Timestamp.fromDate(start),
                fechaFin: Timestamp.fromDate(end),
                tipo: eventType,
                creadoEn: Timestamp.now()
            });
            setShowModal(false);
            setNewEventTitle('');
        } catch (e) {
            console.error(e);
            alert("Error al guardar.");
        }
    };

    const handleDeleteEvent = async () => {
        if (!selectedEvent || selectedEvent.type !== 'personal') return;
        if (!window.confirm("¿Eliminar este evento personal?")) return;
        try {
            await deleteDoc(doc(db, "eventos_proveedor", selectedEvent.id));
            setSelectedEvent(null);
        } catch (e) {
            alert("Error al eliminar.");
        }
    };

    const eventStyleGetter = (event) => ({
        style: {
            backgroundColor: event.color,
            borderRadius: '8px',
            opacity: 0.9,
            color: 'white',
            border: 'none',
            display: 'block',
            padding: '2px 6px',
            fontSize: '0.8rem',
            fontWeight: '600'
        }
    });

    return (
        <div className="ks-page">
            <KondoSidebar activeRoute="/proveedor/calendario" />
            <main className="ks-main">
                <div className="ks-header-sticky-wrapper">
                    <KondoBrandHeader showBack={false} />
                </div>
                <div className="ks-scroll-area">
                    <div className="muro-header-stitch">
                        <div className="muro-hero-card">
                            <div className="muro-hero-content">
                                <h1>Mi Calendario</h1>
                                <p>Agenda personal y servicios</p>
                                <button className="muro-hero-action-btn" onClick={() => setShowModal(true)}>
                                    <IoAddCircle size={20} />
                                    <span>Evento</span>
                                </button>
                            </div>
                            <IoCalendarOutline className="muro-hero-icon-decor" />
                        </div>
                    </div>
                    
                    <div className="calendario-container">
                        <div className="calendar-wrapper">
                            <Calendar
                                localizer={localizer}
                                events={events}
                                startAccessor="start"
                                endAccessor="end"
                                style={{ height: 'calc(100vh - 250px)', minHeight: '500px' }}
                                messages={messages}
                                eventPropGetter={eventStyleGetter}
                                dayPropGetter={dayPropGetter}
                                slotPropGetter={slotPropGetter}
                                onSelectEvent={setSelectedEvent}
                                date={currentDate}
                                view={currentView}
                                onNavigate={date => setCurrentDate(date)}
                                onView={view => setCurrentView(view)}
                                culture='es'
                            />
                        </div>
                    </div>

                    {showModal && (
                        <div className="modal-overlay">
                            <div className="modal-content">
                                <h3>Nuevo Evento Personal</h3>
                                <div className="form-group">
                                    <label>Título</label>
                                    <input value={newEventTitle} onChange={e => setNewEventTitle(e.target.value)} placeholder="Ej: Comida, Cita Médica" />
                                </div>
                                <div className="form-group">
                                    <label>Fecha</label>
                                    <input type="date" value={newEventDate} onChange={e => setNewEventDate(e.target.value)} />
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Inicio</label>
                                        <input type="time" value={newEventStart} onChange={e => setNewEventStart(e.target.value)} />
                                    </div>
                                    <div className="form-group">
                                        <label>Fin</label>
                                        <input type="time" value={newEventEnd} onChange={e => setNewEventEnd(e.target.value)} />
                                    </div>
                                </div>
                                <div className="modal-actions">
                                    <button onClick={() => setShowModal(false)} className="cancel-btn">Cancelar</button>
                                    <button onClick={handleAddEvent} className="save-btn">Guardar Bloqueo</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {selectedEvent && (
                        <div className="modal-overlay" onClick={() => setSelectedEvent(null)}>
                            <div className="modal-content" onClick={e => e.stopPropagation()}>
                                <h3>{selectedEvent.title}</h3>
                                <p style={{ color: '#64748b', marginBottom: '20px' }}>
                                    {moment(selectedEvent.start).format('LLL')} - {moment(selectedEvent.end).format('h:mm a')}
                                </p>
                                {selectedEvent.type === 'personal' && (
                                    <button onClick={handleDeleteEvent} className="delete-btn">
                                        <IoTrash /> Eliminar Evento
                                    </button>
                                )}
                                {selectedEvent.type === 'service' && (
                                    <button onClick={() => navigate(`/proveedor/seguimiento/${selectedEvent.id}`)} className="view-btn">
                                        Ver Detalle del Servicio
                                    </button>
                                )}
                                <button style={{ marginTop: 16, width: '100%', padding: 12, border: 'none', background: '#f1f5f9', borderRadius: '12px', fontWeight: '700', cursor: 'pointer' }} onClick={() => setSelectedEvent(null)}>Cerrar</button>
                            </div>
                        </div>
                    )}
                </div>
                <KondoBottomNav activeRoute="/proveedor/calendario" />
            </main>
        </div>
    );
}

export default CalendarioProveedorScreen;
